import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, CITIES } from "@/lib/api";
import { CalendarDays, MapPin, Users, DollarSign, ArrowRight } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [city, setCity] = useState("All");

  useEffect(() => {
    const p = city !== "All" ? { city } : {};
    api.get("/events", { params: p }).then((r) => setEvents(r.data)).catch(() => {});
  }, [city]);

  return (
    <div className="max-w-7xl mx-auto px-5 py-12">
      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Event Marketplace</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Fairs, festivals & vendor spots</h1>
        <p className="text-slate-500 mt-3 text-lg">Organizers post events. Food truck owners apply and book their spot. Fresh gigs across the Fox Valley.</p>
      </div>

      <div className="flex gap-2 mt-8 flex-wrap">
        <Chip active={city === "All"} onClick={() => setCity("All")}>All cities</Chip>
        {CITIES.map((c) => <Chip key={c} active={city === c} onClick={() => setCity(c)} testid={`event-city-${c}`}>{c}</Chip>)}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {events.map((e, i) => (
          <motion.div key={e.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Link to={`/events/${e.id}`} className="group block bg-white rounded-2xl border border-teal-100 overflow-hidden hover:-translate-y-1 hover:gr-shadow-lg transition-transform" data-testid={`event-card-${e.id}`}>
              <div className="relative h-44 overflow-hidden">
                <img src={e.image_url || FALLBACK} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(ev) => (ev.currentTarget.src = FALLBACK)} />
                <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/90 text-slate-900 text-xs font-bold">{e.city}</span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold leading-snug group-hover:text-[#0FB5A6] transition-colors">{e.title}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-slate-500">
                  <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#0FB5A6]" /> {e.start_date}{e.end_date ? ` – ${e.end_date}` : ""}</p>
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {e.venue}</p>
                  <p className="flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" /> {e.spots_filled}/{e.spots_total} vendor spots filled</p>
                  <p className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-slate-400" /> ${Number(e.vendor_fee).toFixed(0)} vendor fee</p>
                </div>
                <span className="inline-flex items-center gap-1 text-[#0FB5A6] font-bold mt-4 group-hover:gap-2 transition-all">View & apply <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
      {events.length === 0 && <p className="text-slate-400 text-center py-16">No events listed yet.</p>}
    </div>
  );
}

const Chip = ({ children, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${active ? "bg-[#0FB5A6] text-white" : "bg-white text-slate-600 border border-teal-200 hover:bg-teal-50"}`}>{children}</button>
);
